import React from 'react';
import { Cable, Cpu, Wrench, CheckCircle2, ArrowRight, Network } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

export const InfrastructureFibreOptique = () => {
  const services = [
    {
      icon: <Cable className="w-6 h-6" />,
      title: 'Fusion de Fibre Optique',
      description: 'Raccordement par fusion de fibres optiques avec équipements de pointe pour garantir des connexions optimales et durables.',
      features: ['Fusion monomode & multimode', 'Tests et certification', 'Documentation complète']
    },
    {
      icon: <Network className="w-6 h-6" />,
      title: 'Installation de Réseaux',
      description: 'Installation professionnelle de câblage structuré aérien et souterrain pour infrastructures de télécommunications.',
      features: ['Installations aériennes', 'Réseaux souterrains', 'Conduits et accès']
    },
    {
      icon: <Wrench className="w-6 h-6" />,
      title: 'Entretien & Maintenance',
      description: 'Maintenance préventive et corrective de réseaux de fibre optique extérieur pour assurer une performance optimale.',
      features: ['Inspection régulière', 'Réparations urgentes', 'Maintenance préventive']
    },
    {
      icon: <Cpu className="w-6 h-6" />,
      title: 'Tests & Certification',
      description: 'Tests complets et certification de vos infrastructures pour garantir la conformité aux normes de l\'industrie.',
      features: ['Tests OTDR', 'Certification de liens', 'Rapports détaillés']
    }
  ];

  const advantages = [
    'Expertise de plus de 15 ans',
    'Équipements de pointe',
    'Intervention rapide 24/7',
    'Techniciens certifiés',
    'Conformité aux normes',
    'Documentation complète'
  ];

  return (
    <section id="infrastructure-fibre" className="py-24 relative overflow-hidden bg-gradient-to-b from-[#050505] to-[#0A0A0A]">
      {/* Background Effects */}
      <div className="absolute inset-0 network-lines opacity-5"></div>
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-blue-500 opacity-10 blur-[150px] rounded-full"></div>
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-[#FF6B35] opacity-10 blur-[150px] rounded-full"></div>
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF6B35] mb-4 block">
            Télécommunications
          </span>
          <h2 className="text-4xl sm:text-5xl font-black mb-6 tracking-tight">
            Infrastructure <span className="text-[#FF6B35]">Fibre Optique</span>
          </h2>
          <p className="text-lg text-gray-400 max-w-3xl mx-auto">
            Construction, installation, tests et maintenance d&apos;infrastructures de réseaux de télécommunications 
            aériens et souterrains. Des solutions professionnelles pour connecter vos installations.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-[#121212] border border-white/10 rounded-xl p-6 hover:border-[#FF6B35]/50 transition-all duration-300 hover:-translate-y-1 group"
            >
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mb-4 text-white group-hover:scale-110 transition-transform duration-300">
                {service.icon}
              </div>
              <h3 className="text-xl font-bold mb-3 group-hover:text-[#FF6B35] transition-colors">
                {service.title}
              </h3>
              <p className="text-gray-400 text-sm mb-4">
                {service.description}
              </p>
              <ul className="space-y-2">
                {service.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm text-gray-300">
                    <CheckCircle2 className="w-4 h-4 text-[#FF6B35] flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Featured Section */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="glass rounded-2xl p-8 md:p-12 mb-16 border-2 border-blue-500/30 relative overflow-hidden group"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-transparent to-[#FF6B35]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          
          <div className="relative z-10 grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                  <Cable className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold">Entretien de Réseau Extérieur</h3>
              </div>
              <p className="text-gray-400 text-lg mb-6">
                Service complet d&apos;entretien et de maintenance de réseaux de fibre optique extérieur. 
                Nos techniciens certifiés assurent la fiabilité et la performance de vos infrastructures critiques.
              </p>
              <button 
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-[#FF6B35] text-white font-semibold px-8 py-4 rounded-lg hover:bg-[#FF8555] transition-all hover:shadow-[0_0_20px_rgba(255,107,53,0.4)] inline-flex items-center gap-2"
              >
                Demander une soumission
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
            
            {/* Advantages Grid */}
            <div className="grid grid-cols-2 gap-3">
              {advantages.map((advantage, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start gap-2 text-sm"
                >
                  <div className="w-5 h-5 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle2 className="w-3 h-3 text-blue-400" />
                  </div>
                  <span className="text-gray-300">{advantage}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center glass rounded-xl p-8 border border-white/10"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <Network className="w-8 h-8 text-[#FF6B35]" />
            <h3 className="text-2xl font-bold">En savoir plus sur nos services</h3>
          </div>
          <p className="text-gray-400 mb-6 max-w-2xl mx-auto">
            Découvrez l&apos;ensemble de nos services d&apos;infrastructure fibre optique et nos solutions clés en main.
          </p>
          <Link
            to="/infrastructure-fibre-optique"
            className="inline-flex items-center gap-2 bg-transparent border-2 border-[#FF6B35] text-[#FF6B35] font-semibold px-8 py-3 rounded-lg hover:bg-[#FF6B35] hover:text-white transition-all"
          >
            Voir tous les détails
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
